"use client";

import type { ReactNode } from "react";
import { cn } from "../../lib";
import {
	type FieldLabelVariant,
	type FieldLayoutHint,
	FieldShell,
} from "../field-types/field-shell";
import { type MagicCardProps, MagicCards } from "../react-bits/magic-cards";

export interface MagicCardsSingleSelectOption {
	value: string;
	label: string;
	description?: string;
	imageSrc?: string;
	disabled?: boolean;
}

export interface MagicCardsSingleSelectFieldProps {
	id: string;
	label: string;
	value: string | null;
	options: readonly MagicCardsSingleSelectOption[];
	onChange: (value: string) => void;
	description?: string;
	error?: string;
	required?: boolean;
	disabled?: boolean;
	className?: string;
	labelVariant?: FieldLabelVariant;
	layoutHint?: FieldLayoutHint;
	/** Defaults to settingsCompact (Phase 6). Pass hub only when intentional. */
	density?: "hub" | "settingsCompact";
	dimUnselectedOnClick?: boolean;
	/** Trailing label-row slot (e.g. InheritableField origin chip). */
	labelTrailing?: ReactNode;
}

/**
 * Single-select adapter over MagicCards (Train D / Phase 6).
 * Tone/depth and choose_build settings use this **outside** SourcesCollector.
 */
export function MagicCardsSingleSelectField({
	id,
	label,
	value,
	options,
	onChange,
	description,
	error,
	required,
	disabled,
	className,
	labelVariant = "compact",
	layoutHint,
	density = "settingsCompact",
	dimUnselectedOnClick = true,
	labelTrailing,
}: MagicCardsSingleSelectFieldProps) {
	const cards: MagicCardProps[] = options.map((option) => ({
		id: option.value,
		label: option.label,
		title: option.label,
		subtitle: option.description ?? "",
		disabled: disabled || option.disabled,
		image: option.imageSrc
			? { src: option.imageSrc, alt: option.label }
			: undefined,
	}));

	const selectedIndex = cards.findIndex((card) => card.id === value);

	return (
		<FieldShell
			id={id}
			label={label}
			description={description}
			error={error}
			labelVariant={labelVariant}
			layoutHint={layoutHint}
			labelTrailing={labelTrailing}
			className={cn("w-full", className)}
		>
			<select
				id={id}
				className="sr-only"
				aria-hidden={false}
				aria-required={required || undefined}
				disabled={disabled}
				value={value ?? ""}
				onChange={(event) => onChange(event.target.value)}
			>
				<option value="" disabled>
					Select…
				</option>
				{options.map((option) => (
					<option
						key={option.value}
						value={option.value}
						disabled={option.disabled}
					>
						{option.label}
					</option>
				))}
			</select>
			<MagicCards
				cards={cards}
				density={density}
				dimUnselectedOnClick={dimUnselectedOnClick}
				enableSpotlight={false}
				enableParticles={false}
				enableTilt={false}
				enableMagnetism={false}
				initialCollapsedSelectionId={value}
				onCardActivate={(_index, card) => {
					if (disabled || card.disabled) {
						return;
					}
					onChange(card.id);
				}}
				className={cn(
					density === "settingsCompact" && "min-h-[120px]",
					selectedIndex < 0 && "opacity-95",
				)}
			/>
		</FieldShell>
	);
}
